import { isTextModel } from './modelKinds';
import { mediaFailureMessage } from './mediaErrors';
import { useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { Sparkles, Check, RefreshCw } from 'lucide-react';
import { api, type Page, type Schema } from '../api/client';
import { useStudio } from './App';
import { Dialog, Field, Spinner, useApi } from './shared';
import { useTasks, type StudioTask } from './production';

type Content = Record<string, unknown>;
type Kind = 'story_bible' | 'character_profile' | 'episode_brief';
const kinds = {
  story_bible: { title: '故事设定', role: 'writer', roleName: '编剧' },
  character_profile: { title: '人物小传', role: 'character_designer', roleName: '人物设计' },
  episode_brief: { title: '分集简报', role: 'planner', roleName: '规划' },
} as const;
const fields: Record<string, string> = {
  logline: '一句话故事',
  theme: '主题',
  worldRules: '世界规则',
  seasonArc: '系列主线',
  openQuestions: '未决问题',
  endingPlan: '计划中的终点',
  name: '人物',
  dramaticRole: '叙事作用',
  desire: '欲望',
  fear: '恐惧',
  flaw: '缺陷',
  secret: '秘密',
  speakingStyle: '说话方式',
  growthPlan: '成长计划',
  immutableTraits: '不变特征',
  relationships: '人物关系',
  goal: '本集目标',
  openingHook: '开场钩子',
  endingHook: '结尾悬念',
  targetSeconds: '目标秒数',
  plannedStateChanges: '计划中的变化',
  unresolvedQuestions: '未解决的问题',
};
function display(value: unknown): string {
  if (Array.isArray(value)) return value.map(display).join('\n') || '暂无';
  if (value && typeof value === 'object') {
    const row = value as Content;
    return String(row.statement ?? row.relationship ?? JSON.stringify(row));
  }
  return String(value ?? '');
}

export function DesignButton({
  kind,
  currentDraft,
  characterId,
  episodeId,
  onAdopt,
  disabled,
}: {
  kind: Kind;
  currentDraft: Content;
  characterId?: string;
  episodeId?: string;
  onAdopt: (content: Content) => void;
  disabled?: boolean;
}) {
  const [frozen, setFrozen] = useState<Content | null>(null);
  return (
    <>
      <button
        type="button"
        className="button secondary"
        disabled={disabled}
        onClick={() => setFrozen(structuredClone(currentDraft))}
      >
        <Sparkles size={16} />
        AI 设计{kinds[kind].title}
      </button>
      {frozen && (
        <DesignDialog
          kind={kind}
          frozen={frozen}
          currentDraft={currentDraft}
          {...(characterId ? { characterId } : {})}
          {...(episodeId ? { episodeId } : {})}
          close={() => setFrozen(null)}
          onAdopt={onAdopt}
        />
      )}
    </>
  );
}

function DesignDialog({
  kind,
  frozen,
  currentDraft,
  characterId,
  episodeId,
  close,
  onAdopt,
}: {
  kind: Kind;
  frozen: Content;
  currentDraft: Content;
  characterId?: string;
  episodeId?: string;
  close: () => void;
  onAdopt: (content: Content) => void;
}) {
  const { base, project, branch, toast, go } = useStudio();
  const qc = useQueryClient();
  const [modelId, setModelId] = useState(''),
    [prompt, setPrompt] = useState(''),
    [busy, setBusy] = useState(false),
    [error, setError] = useState(''),
    [chosenId, setChosenId] = useState('');
  const role = kinds[kind];
  const models = useApi<Page<Schema<'ModelSummary'>>>(`${base}/models?role=${role.role}`);
  const policy = useApi<Schema<'ModelPolicy'>>(`${base}/series/${project?.id}/model-policy`);
  const settings = useApi<{ callsEnabled: boolean }>(`${base}/model-settings`);
  const path = `${base}/studio-tasks?seriesId=${project?.id}&branchId=${branch?.id}`;
  const tasks = useTasks(path);
  const matching =
    tasks.data?.items.filter(
      (t) =>
        t.kind === 'design' &&
        t.artifactKind === kind &&
        (t.characterId || '') === (characterId || '') &&
        (t.episodeId || '') === (episodeId || ''),
    ) ?? [];
  const active = matching.find((t) => ['queued', 'running'].includes(t.state));
  const chosen = matching.find((t) => t.id === chosenId) ?? active ?? matching[0];
  const options = models.data?.items.filter((m) => m.selectable && isTextModel(m)) ?? [];
  const preferred = policy.data?.bindings[role.role]?.primary.catalogModelId;
  // An unavailable configured role stays visible; changing models requires an explicit choice.
  const selectedId = modelId || preferred || options[0]?.catalogModelId || '';
  const selected = options.find((m) => m.catalogModelId === selectedId);
  const changed = JSON.stringify(currentDraft) !== JSON.stringify(frozen);
  const proposal = chosen?.state === 'completed' ? chosen.result.designProposal : undefined;
  const act = async (fn: () => Promise<void>) => {
    setBusy(true);
    setError('');
    try {
      await fn();
      await qc.invalidateQueries({ queryKey: ['api', path] });
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setBusy(false);
    }
  };
  return (
    <Dialog
      title={`AI 设计${role.title}`}
      subtitle="先审阅候选，再采用到当前表单；保存与定稿仍由你决定。"
      close={close}
    >
      <div className="dialog-body design-assistant">
        <details open={!proposal} className="design-controls-panel">
          <summary>
            {proposal ? '再次生成 · ' : ''}创作要求与模型 · {selected?.displayName ?? role.roleName}
          </summary>
          <div className="design-controls">
            <Field label={`使用模型 · ${role.roleName}`}>
              <select
                value={selectedId}
                onChange={(e) => setModelId(e.target.value)}
                disabled={busy || !!active}
              >
                {!selected && (
                  <option value={selectedId}>
                    {selectedId ? '该角色绑定的模型当前不可用，请重新选择' : '暂无可用模型'}
                  </option>
                )}
                {options.map((m) => (
                  <option key={m.catalogModelId} value={m.catalogModelId}>
                    {m.displayName}
                  </option>
                ))}
              </select>
            </Field>
            <Field label="创作要求" hint="会同时参考本分支已定稿的背景，以及打开面板时的草稿。">
              <textarea
                rows={3}
                maxLength={16000}
                value={prompt}
                disabled={busy || !!active}
                onChange={(e) => setPrompt(e.target.value)}
                placeholder={`描述希望生成或改进的${role.title}，例如加强悬念并适合每集一分钟。`}
              />
            </Field>
            {!selected && <p className="form-error">请选择已通过测试的文字模型。</p>}
            {!settings.data?.callsEnabled && settings.isSuccess && (
              <p className="form-error">服务器尚未启用模型调用。</p>
            )}
            {!selected && (
              <button
                type="button"
                className="button small secondary"
                onClick={() => {
                  close();
                  go('models');
                }}
              >
                去配置模型
              </button>
            )}
            <button
              type="button"
              className="button primary"
              disabled={
                busy ||
                !!active ||
                !selected ||
                !prompt.trim() ||
                !settings.data?.callsEnabled ||
                policy.isPending ||
                !!policy.error ||
                models.isPending ||
                tasks.isPending ||
                !!tasks.error ||
                !project ||
                !branch
              }
              onClick={() =>
                void act(async () => {
                  const task = await api<StudioTask>(`${base}/studio-tasks`, 'POST', {
                    kind: 'design',
                    seriesId: project!.id,
                    branchId: branch!.id,
                    artifactKind: kind,
                    ...(characterId ? { characterId } : {}),
                    ...(episodeId ? { episodeId } : {}),
                    modelId: selectedId,
                    prompt,
                    currentDraft: frozen,
                    confirmed: true,
                  });
                  setChosenId(task.id);
                })
              }
            >
              {busy || active ? <Spinner /> : <Sparkles size={16} />}
              {active ? '正在生成，可关闭后回来查看' : '生成候选稿'}
            </button>
          </div>
        </details>
        {active && (
          <p role="status">
            <Spinner />
            正在生成候选，关闭面板后任务仍会继续。
          </p>
        )}
        {(error || models.error || policy.error || settings.error || tasks.error) && (
          <p className="form-error" role="alert">
            {error || (models.error ?? policy.error ?? settings.error ?? tasks.error)?.message}
          </p>
        )}
        {matching.length > 0 && (
          <Field label="生成记录">
            <select value={chosen?.id ?? ''} onChange={(e) => setChosenId(e.target.value)}>
              {matching.map((t) => (
                <option key={t.id} value={t.id}>
                  {new Date(t.createdAt).toLocaleString()} ·{' '}
                  {{
                    completed: '已完成',
                    running: '生成中',
                    queued: '等待中',
                    failed: '失败',
                    outcome_unknown: '结果待核对',
                    cancelled: '已取消',
                  }[t.state] ?? t.state}
                </option>
              ))}
            </select>
          </Field>
        )}
        {chosen?.errorMessage && (
          <div className="info-box amber">
            <p>{mediaFailureMessage(chosen)}</p>
            {chosen.canRecover && (
              <button
                type="button"
                className="button small secondary"
                disabled={busy}
                onClick={() =>
                  void act(async () => {
                    await api(`${base}/studio-tasks/${chosen.id}/recover`, 'POST', {});
                  })
                }
              >
                <RefreshCw size={14} />
                恢复原结果
              </button>
            )}
          </div>
        )}
        {chosen?.result.text && (
          <details>
            <summary>查看保留的原始回复</summary>
            <pre className="design-raw">{chosen.result.text}</pre>
          </details>
        )}
        {proposal && (
          <>
            <p className="quiet">
              候选来自 {proposal.model.upstreamModelId} · 使用生成时的故事背景
            </p>
            <dl className="design-preview">
              {Object.entries(proposal.content)
                .filter(([key]) => fields[key])
                .map(([key, value]) => (
                  <div key={key}>
                    <dt>{fields[key]}</dt>
                    <dd>{display(value)}</dd>
                  </div>
                ))}
            </dl>
            {changed && (
              <p className="form-error">
                当前表单已发生变化。请关闭后重新打开面板，核对草稿再采用历史候选。
              </p>
            )}
            <button
              type="button"
              className="button primary design-adopt"
              disabled={
                busy ||
                changed ||
                proposal.artifactKind !== kind ||
                proposal.scope.seriesId !== project?.id ||
                proposal.scope.branchId !== branch?.id ||
                (proposal.scope.characterId || '') !== (characterId || '') ||
                (proposal.scope.episodeId || '') !== (episodeId || '')
              }
              onClick={() => {
                onAdopt(structuredClone(proposal.content));
                toast('候选已填入表单，请审阅并保存');
                close();
              }}
            >
              <Check size={16} />
              采用到当前表单
            </button>
          </>
        )}
      </div>
    </Dialog>
  );
}
